import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Alert from '@amzn/meridian/alert';
import { FloorplanViewer, FloorplanProvider, PolygonLayer } from '@amzn/ihm-floorplan-viewer';
import { BayData } from '@amzn/ihm-floorplan-core';
import { transformFPData } from '../zoneViewer/zoneMapDataHelper';
import { usePickPathDrawing } from '../hooks/usePickPathDrawing';
import useUpdateFloorplan from 'src/store/hooks/useUpdateFloorplan';
import { useFlowLabel } from '../utils/flowLabel';
import { ZoneCreationProperties } from '../types/zoneMapType';
import useDisplayModes from '../zoneViewer/displayModes';
import SelectionToolbar from '../zoneViewer/SelectionToolbar';

import logger from '@utils/logger';
import { SequenceDrawingViewProps } from './types';

const SequenceDrawingView: React.FC<SequenceDrawingViewProps> = ({
  storeId,
  department,
  floorplanData,
  onSaveComplete,
  onBack,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync: updateFloorplanPickPath } = useUpdateFloorplan();
  const flowLabel = useFlowLabel();
  const floorplanId = floorplanData.length > 0 ? floorplanData[0].floorplanId : undefined;

  const fixtures = useMemo(() => (floorplanData.length > 0 ? (floorplanData[0].fixtures ?? []) : []), [floorplanData]);

  // Filter fixtures to selected department
  const departmentFixtures = useMemo(
    () => (department ? fixtures.filter((f) => f.department === department) : fixtures),
    [fixtures, department],
  );

  const fpData = useMemo(() => transformFPData(departmentFixtures), [departmentFixtures]);

  // Zone creation properties for the pick path drawing hook
  const [zoneCreationProperties, setZoneCreationProperties] = useState<Map<number, ZoneCreationProperties>>(() => {
    const map = new Map<number, ZoneCreationProperties>();
    map.set(0, {
      zoneIdx: 0,
      zoneName: '',
      productGroup: '',
      categoryId: '',
      subcategoryIds: [],
      subcategoryNames: [],
      fixtureIds: [],
      facings: 0,
      pickPaths: [],
    });
    return map;
  });

  const pickPathDrawing = usePickPathDrawing({
    zoneCreationProperties,
    setZoneCreationProperties,
    activeZoneIdx: 0,
    fixtureData: fpData,
  });

  // Build ordering map from drawn paths
  const editingOrderingMap = useMemo(() => {
    const map = new Map<string, number>();
    let seq = 1;
    for (const zone of zoneCreationProperties.values()) {
      for (const path of zone.pickPaths ?? []) {
        for (const wp of path.waypoints) {
          map.set(wp.fixtureId, seq++);
        }
      }
    }
    if (pickPathDrawing.inProgressPath) {
      for (const wp of pickPathDrawing.inProgressPath.waypoints) {
        map.set(wp.fixtureId, seq++);
      }
    }
    return map;
  }, [zoneCreationProperties, pickPathDrawing.inProgressPath]);

  const hasPickPaths = useMemo(() => {
    for (const zone of zoneCreationProperties.values()) {
      if ((zone.pickPaths ?? []).length > 0) return true;
    }
    return false;
  }, [zoneCreationProperties]);

  const fixtureDepartmentMap = useMemo(() => {
    const map = new Map<string, string>();
    fpData.forEach((f) => {
      if (f.id && f.department) map.set(f.id, f.department);
    });
    return map;
  }, [fpData]);

  const displayModes = useDisplayModes({}, undefined, undefined, fixtureDepartmentMap, editingOrderingMap);
  const displayMode = useMemo(() => {
    const pickSeq = displayModes.optionsList.find((m) => m.id === 'PICK_SEQUENCE');
    return pickSeq ?? displayModes.defaultOption;
  }, [displayModes]);

  // Handle Escape key and Ctrl+Z/Cmd+Z
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pickPathDrawing.isDrawing) {
        pickPathDrawing.stopDrawing();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && pickPathDrawing.isDrawing) {
        e.preventDefault();
        pickPathDrawing.undoLastWaypoint();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pickPathDrawing]);

  // Attach pointerup to window for drag-to-draw release detection
  useEffect(() => {
    if (!pickPathDrawing.isDrawing) return;
    window.addEventListener('pointerup', pickPathDrawing.onPointerUp);
    return () => window.removeEventListener('pointerup', pickPathDrawing.onPointerUp);
  }, [pickPathDrawing.isDrawing, pickPathDrawing.onPointerUp]);

  const handleSave = useCallback(async () => {
    if (editingOrderingMap.size < departmentFixtures.length) {
      setError(
        `${flowLabel.sentence} must cover all ${departmentFixtures.length} fixtures (currently ${editingOrderingMap.size}).`,
      );
      return;
    }

    if (!floorplanId) {
      setError(`Cannot save ${flowLabel.lower}: no floorplan loaded.`);
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const fixtures = Array.from(editingOrderingMap.entries()).map(([fixtureId, sequenceOrder]) => ({
        fixtureId,
        pickPathId: String(sequenceOrder),
      }));

      await updateFloorplanPickPath({
        input: {
          storeId,
          floorplanId,
          fixtures,
        },
      });

      onSaveComplete(storeId, editingOrderingMap);
    } catch (err) {
      const msg = err instanceof Error ? err.message : `Failed to save ${flowLabel.lower}`;
      setError(msg);
      void logger.error(
        `Failed to save ${flowLabel.lower} for target store`,
        err instanceof Error ? err : new Error(msg),
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    editingOrderingMap,
    departmentFixtures,
    updateFloorplanPickPath,
    storeId,
    floorplanId,
    onSaveComplete,
    flowLabel,
  ]);

  const pickPathOverlay =
    pickPathDrawing.pickPathFeatureCollection.features.length > 0 ? (
      <PolygonLayer
        data={pickPathDrawing.pickPathFeatureCollection}
        defaultStyle={{ showArrows: true, outlineStyle: 'solid' }}
      />
    ) : null;

  return (
    <Column height="100%" width="100%">
      <Row spacingInset="400" alignmentHorizontal="justify" alignmentVertical="center">
        <Heading level={4}>Draw Sequence — Store {storeId}</Heading>
        <Row spacing="300">
          <Button type="secondary" onClick={onBack}>
            Back to Gallery
          </Button>
          {hasPickPaths && !pickPathDrawing.isDrawing && (
            <Button type="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Row>
      </Row>

      {error && (
        <Row spacingInset="0 400">
          <Alert type="error">{error}</Alert>
        </Row>
      )}

      <Text type="b100" color="secondary">
        {department ? `Department: ${department} — ` : ''}
        {departmentFixtures.length} fixtures total, {editingOrderingMap.size} sequenced
      </Text>

      <SelectionToolbar
        activeZoneIdx={0}
        zones={zoneCreationProperties}
        onActiveZoneChange={() => {}}
        disabled={false}
        isDrawingPickPath={pickPathDrawing.isDrawing}
        onStartDrawing={pickPathDrawing.startDrawing}
        onStopDrawing={pickPathDrawing.stopDrawing}
        onFinishPath={pickPathDrawing.finishPath}
        onSubmitSequence={handleSave}
        hasPickPaths={hasPickPaths}
        onUndoWaypoint={pickPathDrawing.undoLastWaypoint}
        canUndo={(pickPathDrawing.inProgressPath?.waypoints.length ?? 0) > 0}
        totalFixtureCount={departmentFixtures.length}
        sequencedFixtureCount={editingOrderingMap.size}
        flowLabel={flowLabel}
      />

      <Column height="100%">
        {fpData.length > 0 && displayMode && (
          <FloorplanProvider>
            <FloorplanViewer
              fixtureData={fpData}
              displayMode={displayMode}
              onFixtureHover={
                pickPathDrawing.isDrawing
                  ? (fixture: BayData | null) => {
                      if (fixture?.id) pickPathDrawing.handleFixturePointerOver(fixture.id);
                    }
                  : undefined
              }
              onFixturePointerDown={
                pickPathDrawing.isDrawing
                  ? (fixture: BayData) => {
                      if (fixture.id) pickPathDrawing.onPointerDown(fixture.id);
                    }
                  : undefined
              }
              enablePan={!pickPathDrawing.isDrawing}
            >
              {pickPathOverlay}
            </FloorplanViewer>
          </FloorplanProvider>
        )}
      </Column>
    </Column>
  );
};

export default SequenceDrawingView;
